import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { apiErr, formatNum } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, Fuel, ShieldCheck, Plus, Trash2, Pencil, AlertTriangle, GitBranch, FileText,
  Loader2, CheckCircle2, Gauge, Download, Route as RouteIcon,
  Upload, History, Leaf, FlaskConical, TrendingUp,
} from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";

const WRITE_ROLES = ["tenant_admin", "esg_manager", "data_officer"];
const fmt = (n, d = 3) => formatNum(n || 0, { decimals: d });

const scopeBadge = (scope, s3) =>
  scope === 1
    ? <Badge variant="success" className="text-[10px]">Scope 1</Badge>
    : <Badge variant="warning" className="text-[10px]">Scope 3{s3 ? ` · Cat ${s3}` : ""}</Badge>;

const flagLabel = {
  owned: "Owned", leased_operated: "Leased — we operate",
  leased_third_party: "Leased — third party", hired_service: "Hired service",
};

const selectCls = "w-full text-sm rounded-md border border-border bg-transparent p-2 focus:outline-none focus:ring-1 focus:ring-accent";
const inputCls = selectCls;

export default function MobileCombustion() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [ref, setRef] = useState(null);
  const [assets, setAssets] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [boundary, setBoundary] = useState(null);
  const [electrify, setElectrify] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [tab, setTab] = useState("assets");
  const [assetDlg, setAssetDlg] = useState(null);   // {} for new, asset for edit
  const [fuelDlg, setFuelDlg] = useState(false);
  const [spendDlg, setSpendDlg] = useState(false);
  const [importDlg, setImportDlg] = useState(null);   // "assets" | "fuel"
  const [lineageId, setLineageId] = useState(null);

  const load = useCallback(() => {
    api.get("/mobile-combustion/reference").then((r) => setRef(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/mobile-combustion/assets").then((r) => setAssets(r.data.assets || [])).catch(() => {});
    api.get("/mobile-combustion/fuel").then((r) => setRecords(r.data.records || [])).catch(() => {});
    api.get("/mobile-combustion/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/mobile-combustion/boundary-check").then((r) => setBoundary(r.data)).catch(() => {});
    api.get(`/mobile-combustion/electrification?year=${new Date().getFullYear()}`).then((r) => setElectrify(r.data)).catch(() => {});
    api.get("/mobile-combustion/review-reminders").then((r) => setReminders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => load();

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="mobile-combustion-page">
      <div>
        <div className="label-eyebrow mb-1">Data Management · GHG Protocol</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
          <Truck className="h-8 w-8 text-accent" /> Mobile Combustion
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Vehicles &amp; mobile machinery fuel — classified once per asset by operational control, so the
          Scope 1 vs Scope 3 boundary is decided, auditable and impossible to double-count. Fuel entered in
          litres, kg or energy converts credibly; a distance-based method is available; Scope 3 uses life-cycle factors.
        </p>
      </div>

      {reminders?.overdue?.length > 0 && (
        <div className="card-flat p-4 border border-[#FFB800]/50 bg-[#FFB800]/5" data-testid="mc-review-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-[#FFB800]" />
            <span className="font-semibold text-sm">{reminders.overdue_count} asset control-review{reminders.overdue_count === 1 ? "" : "s"} overdue</span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">Re-confirm each asset's operational-control classification so the Scope 1/3 boundary stays correct.</p>
          <div className="space-y-1.5">
            {reminders.overdue.map((o) => (
              <div key={o.asset_id} className="flex items-center gap-3 text-xs" data-testid={`mc-review-item-${o.asset_id}`}>
                <span className="font-medium">{o.asset_ref}</span>
                <span className="text-muted-foreground flex-1 min-w-0 truncate">{o.review_note || o.description} · due {o.review_due_date || "—"}</span>
                {canWrite && (
                  <Button size="sm" variant="outline" data-testid={`mc-mark-reviewed-${o.asset_id}`}
                    onClick={async () => {
                      try { await api.post(`/mobile-combustion/assets/${o.asset_id}/mark-reviewed`); toast.success("Marked reviewed."); refresh(); }
                      catch (e) { toast.error(apiErr(e)); }
                    }}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark reviewed
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="assets" data-testid="mc-tab-assets">Asset Register</TabsTrigger>
          <TabsTrigger value="fuel" data-testid="mc-tab-fuel">Fuel Records</TabsTrigger>
          <TabsTrigger value="trends" data-testid="mc-tab-trends">Per-vehicle Trends</TabsTrigger>
          <TabsTrigger value="boundary" data-testid="mc-tab-boundary">Boundary &amp; Scope</TabsTrigger>
          <TabsTrigger value="electrify" data-testid="mc-tab-electrify">Electrification</TabsTrigger>
        </TabsList>

        {/* ---------------- ASSET REGISTER ---------------- */}
        <TabsContent value="assets" className="mt-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {assets.length} asset{assets.length === 1 ? "" : "s"} · control classification decides the scope boundary.
            </div>
            {canWrite && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setImportDlg("assets")} data-testid="mc-import-btn">
                  <Upload className="h-3.5 w-3.5" /> Import
                </Button>
                <Button size="sm" variant="primary" onClick={() => setAssetDlg({})} data-testid="mc-add-asset-btn">
                  <Plus className="h-3.5 w-3.5" /> Add asset
                </Button>
              </div>
            )}
          </div>
          <div className="card-flat overflow-x-auto" data-testid="mc-assets-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Asset</TableHead><TableHead>Class</TableHead><TableHead>Control</TableHead>
                <TableHead>Boundary</TableHead><TableHead className="text-right">Records</TableHead>
                <TableHead className="text-right">tCO₂e</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {assets.map((a) => (
                  <TableRow key={a.id} data-testid={`mc-asset-row-${a.id}`}>
                    <TableCell>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {a.asset_ref}
                        {a.review_flag && <Badge variant="warning" className="text-[9px]" data-testid={`mc-review-${a.id}`}>Review due</Badge>}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{a.description}</div>
                    </TableCell>
                    <TableCell className="text-xs capitalize">{a.asset_class}<div className="text-[10px] text-muted-foreground">{a.road_type === "non_road" ? "non-road" : "on-road"}</div></TableCell>
                    <TableCell className="text-xs">{flagLabel[a.control_flag] || a.control_flag}</TableCell>
                    <TableCell>{scopeBadge(a.derived_scope, a.derived_s3_cat)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{a.records}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(a.co2e_tonnes)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canWrite && <Button size="sm" variant="ghost" onClick={() => setAssetDlg(a)} data-testid={`mc-edit-asset-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                      {canWrite && <Button size="sm" variant="ghost" onClick={() => onDeleteAsset(a, refresh)} data-testid={`mc-delete-asset-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!assets.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8 text-sm">No assets registered yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------------- FUEL RECORDS ---------------- */}
        <TabsContent value="fuel" className="mt-5 space-y-4">
          <FuelRecords
            records={records} assets={assets} canWrite={canWrite}
            onImport={() => setImportDlg("fuel")} onSpend={() => setSpendDlg(true)}
            onLog={() => setFuelDlg(true)} onLineage={(id) => setLineageId(id)}
            onRefresh={refresh}
          />
        </TabsContent>

        {/* ---------------- PER-VEHICLE TRENDS ---------------- */}
        <TabsContent value="trends" className="mt-5 space-y-4">
          <AssetTrend assets={assets} />
        </TabsContent>

        {/* ---------------- BOUNDARY & SCOPE ---------------- */}
        <TabsContent value="boundary" className="mt-5 space-y-5">
          {summary && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="card-flat p-4" data-testid="mc-sum-scope1">
                <div className="label-eyebrow text-[10px] mb-1">Scope 1 — owned / we operate</div>
                <div className="font-mono text-2xl font-bold text-accent">{fmt(summary.scope1.co2e)} <span className="text-xs text-muted-foreground">tCO₂e</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">{summary.scope1.records} records</div>
              </div>
              <div className="card-flat p-4" data-testid="mc-sum-scope3">
                <div className="label-eyebrow text-[10px] mb-1">Scope 3 — leased / hired</div>
                <div className="font-mono text-2xl font-bold">{fmt(summary.scope3_total)} <span className="text-xs text-muted-foreground">tCO₂e</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">{summary.scope3.map((b) => `${b.category}: ${fmt(b.co2e)}t`).join(" · ") || "none"}</div>
              </div>
              <div className="card-flat p-4">
                <div className="label-eyebrow text-[10px] mb-1">Total mobile combustion</div>
                <div className="font-mono text-2xl font-bold">{fmt(summary.total)} <span className="text-xs text-muted-foreground">tCO₂e</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">life-cycle factors on Scope 3</div>
              </div>
            </div>
          )}

          {boundary && (
            <div className={`card-flat p-4 border ${boundary.consistent ? "border-[#00E65B]/40" : "border-red-500/50"}`} data-testid="mc-boundary-check">
              <div className="flex items-center gap-2 mb-2">
                {boundary.consistent
                  ? <><CheckCircle2 className="h-4 w-4 text-[#00E65B]" /><span className="font-semibold text-sm">Boundary consistent — no double-count</span></>
                  : <><AlertTriangle className="h-4 w-4 text-red-400" /><span className="font-semibold text-sm">Boundary issues found</span></>}
              </div>
              <p className="text-[11px] text-muted-foreground">{boundary.checked_note} ({boundary.assets_checked} assets checked)</p>
              {boundary.double_count_violations?.map((v, i) => (
                <div key={i} className="mt-2 text-xs text-red-400" data-testid="mc-violation">⚠ {v.asset_ref}: {v.note}</div>
              ))}
              {boundary.duplicate_asset_months?.map((v, i) => (
                <div key={`dup-${i}`} className="mt-2 text-xs text-red-400" data-testid="mc-duplicate-month">⚠ {v.note}</div>
              ))}
              {boundary.stale_routing?.map((v, i) => (
                <div key={i} className="mt-2 text-xs text-[#FFB800]" data-testid="mc-stale">⚠ {v.asset_ref} ({v.period}): {v.note}</div>
              ))}
            </div>
          )}

          {summary?.unassigned?.length > 0 && (
            <div className="card-flat p-4" data-testid="mc-unassigned">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-[#FFB800]" /><span className="font-semibold text-sm">Unassigned fuel records ({summary.unassigned_count}) — data-quality gap</span></div>
              <p className="text-[11px] text-muted-foreground mb-2">These mobile-combustion records aren't linked to a registered asset, so their scope boundary is undecided. Assign each to an asset.</p>
              {summary.unassigned.map((u) => (
                <BackfillRow key={u.id} row={u} assets={assets} onDone={refresh} canWrite={canWrite} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- FLEET ELECTRIFICATION ---------------- */}
        <TabsContent value="electrify" className="mt-5 space-y-5">
          {electrify && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="card-flat p-4" data-testid="mc-elec-fleet">
                  <div className="label-eyebrow text-[10px] mb-1">Fleet emissions (annual)</div>
                  <div className="font-mono text-2xl font-bold">{fmt(electrify.fleet_co2e_t)} <span className="text-xs text-muted-foreground">tCO₂e</span></div>
                </div>
                <div className="card-flat p-4 border border-[#00E65B]/30" data-testid="mc-elec-abatement">
                  <div className="label-eyebrow text-[10px] mb-1">Addressable abatement</div>
                  <div className="font-mono text-2xl font-bold text-accent">{fmt(electrify.addressable_abatement_t)} <span className="text-xs text-muted-foreground">tCO₂e/yr</span></div>
                  <div className="text-[11px] text-muted-foreground mt-1">if EV-ready assets switch to electric</div>
                </div>
                <div className="card-flat p-4">
                  <div className="label-eyebrow text-[10px] mb-1">Priority candidates</div>
                  <div className="font-mono text-2xl font-bold">{electrify.candidate_count}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">EV-available &amp; net-positive</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card-flat p-4" data-testid="mc-elec-capex">
                  <div className="label-eyebrow text-[10px] mb-1">Indicative EV capex (candidates)</div>
                  <div className="font-mono text-xl font-bold">S${fmt(electrify.total_capex_sgd, 0)}</div>
                </div>
                <div className="card-flat p-4" data-testid="mc-elec-saving">
                  <div className="label-eyebrow text-[10px] mb-1">Indicative annual fuel saving</div>
                  <div className="font-mono text-xl font-bold text-accent">S${fmt(electrify.total_annual_saving_sgd, 0)}/yr</div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Leaf className="h-3.5 w-3.5 text-accent" /> {electrify.grid_note} Grid {electrify.grid_factor} kgCO₂e/kWh · tariff S${electrify.roi_assumptions?.electricity_tariff_sgd_per_kwh}/kWh · {electrify.roi_assumptions?.asset_life_years}-yr life. {electrify.roi_assumptions?.note}</p>
              <div className="card-flat overflow-x-auto" data-testid="mc-elec-table">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>#</TableHead><TableHead>Asset</TableHead><TableHead>Class</TableHead>
                    <TableHead className="text-right">Distance/hrs</TableHead><TableHead className="text-right">tCO₂e/yr</TableHead>
                    <TableHead className="text-right">EV kWh/yr</TableHead><TableHead className="text-right">Abatement</TableHead>
                    <TableHead className="text-right">EV capex</TableHead><TableHead className="text-right">Payback</TableHead>
                    <TableHead className="text-right">S$/t</TableHead><TableHead>Feasibility</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {electrify.assets.map((a) => (
                      <TableRow key={a.asset_id} data-testid={`mc-elec-row-${a.asset_id}`}>
                        <TableCell className="font-mono text-xs">{a.rank}</TableCell>
                        <TableCell><div className="font-medium text-sm">{a.asset_ref}</div><div className="text-[10px] text-muted-foreground">{a.description}</div></TableCell>
                        <TableCell className="text-xs capitalize">{a.asset_class}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{a.annual_distance_km ? `${fmt(a.annual_distance_km, 0)} km` : a.annual_engine_hours ? `${fmt(a.annual_engine_hours, 0)} h` : "—"}<div className="text-[9px] text-muted-foreground">{a.utilisation_source}</div></TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmt(a.annual_co2e_t)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{a.ev_available ? fmt(a.ev_kwh_per_year, 0) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-bold text-accent">{a.ev_available ? fmt(a.abatement_t) : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{a.roi ? `S$${fmt(a.roi.ev_capex_sgd, 0)}` : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{a.roi?.payback_years ? `${a.roi.payback_years} yr` : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{a.roi?.cost_per_tonne_sgd ? `S$${fmt(a.roi.cost_per_tonne_sgd, 0)}` : "—"}</TableCell>
                        <TableCell>
                          {a.ev_available
                            ? <Badge variant={a.ease === "High" ? "success" : a.ease === "Medium" ? "warning" : "outline"} className="text-[9px]">{a.ease}</Badge>
                            : <Badge variant="outline" className="text-[9px]">No EV yet</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-[11px] text-muted-foreground">Ranked by net annual abatement — feed the top candidates into the Investment Register (e.g. the "Electric mower &amp; van fleet transition" initiative).</p>
            </>
          )}
        </TabsContent>
      </Tabs>

      {assetDlg !== null && (
        <AssetDialog asset={assetDlg} ref_={ref} onClose={() => setAssetDlg(null)} onSaved={() => { setAssetDlg(null); refresh(); }} />
      )}
      {fuelDlg && ref && (
        <FuelDialog ref_={ref} assets={assets} onClose={() => setFuelDlg(false)} onSaved={() => { setFuelDlg(false); refresh(); }} />
      )}
      {spendDlg && (
        <SpendDialog onClose={() => setSpendDlg(false)} onSaved={() => { setSpendDlg(false); refresh(); }} />
      )}
      {importDlg && (
        <ImportDialog mode={importDlg} onClose={() => setImportDlg(null)} onDone={() => { setImportDlg(null); refresh(); }} />
      )}
      {lineageId && <LineageDialog pointId={lineageId} onClose={() => setLineageId(null)} />}
    </div>
  );
}

async function onDeleteAsset(a, refresh) {
  if (!window.confirm(`Delete asset ${a.asset_ref}?`)) return;
  try { await api.delete(`/mobile-combustion/assets/${a.id}`); toast.success("Asset deleted."); refresh(); }
  catch (e) { toast.error(apiErr(e)); }
}

function FuelRecords({ records, assets, canWrite, onImport, onSpend, onLog, onLineage, onRefresh }) {
  const [periodFilter, setPeriodFilter] = useState("");
  const [assetFilter, setAssetFilter] = useState("");
  const [selected, setSelected] = useState({});   // {id: true}
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const periods = Array.from(new Set(records.map((r) => r.period).filter(Boolean))).sort().reverse();
  const assetOpts = Array.from(new Map(records.filter((r) => r.asset_id)
    .map((r) => [r.asset_id, r.asset_ref || r.asset_id])).entries());

  const filtered = records.filter((r) =>
    (!periodFilter || r.period === periodFilter) && (!assetFilter || r.asset_id === assetFilter));

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const visibleSelectedCount = filtered.filter((r) => selected[r.id]).length;
  const allVisibleSelected = filtered.length > 0 && visibleSelectedCount === filtered.length;

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    if (allVisibleSelected) { setSelected({}); return; }
    const next = {};
    filtered.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const ids = filtered.filter((r) => selected[r.id]).map((r) => r.id);
      const { data } = await api.post("/mobile-combustion/fuel/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} fuel record${data.deleted === 1 ? "" : "s"} — logged in Change history.`);
      setSelected({}); setConfirmDelete(false); onRefresh();
    } catch (e) { toast.error(apiErr(e, "Couldn't delete the selected records — please try again.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select className={selectCls} style={{ width: 150 }} value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} data-testid="mc-fuel-filter-period">
            <option value="">All periods</option>
            {periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className={selectCls} style={{ width: 180 }} value={assetFilter} onChange={(e) => setAssetFilter(e.target.value)} data-testid="mc-fuel-filter-asset">
            <option value="">All assets</option>
            {assetOpts.map(([id, ref]) => <option key={id} value={id}>{ref}</option>)}
          </select>
          {(periodFilter || assetFilter) && (
            <button className="text-[11px] text-accent hover:underline" onClick={() => { setPeriodFilter(""); setAssetFilter(""); }} data-testid="mc-fuel-filter-clear">Clear</button>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {canWrite && visibleSelectedCount > 0 && (
            <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(true)} data-testid="mc-fuel-delete-btn">
              <Trash2 className="h-3.5 w-3.5" /> Delete {visibleSelectedCount}
            </Button>
          )}
          {canWrite && <Button size="sm" variant="outline" onClick={onImport} data-testid="mc-fuel-import-btn"><Upload className="h-3.5 w-3.5" /> Import fuel</Button>}
          {canWrite && <Button size="sm" variant="outline" onClick={onSpend} data-testid="mc-add-spend-btn"><Plus className="h-3.5 w-3.5" /> Hired spend (Scope 3)</Button>}
          {canWrite && <Button size="sm" variant="primary" onClick={onLog} data-testid="mc-add-fuel-btn"><Fuel className="h-3.5 w-3.5" /> Log fuel</Button>}
        </div>
      </div>
      <div className="card-flat overflow-x-auto" data-testid="mc-fuel-table">
        <Table>
          <TableHeader><TableRow>
            {canWrite && <TableHead className="w-8"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" data-testid="mc-fuel-select-all" /></TableHead>}
            <TableHead>Period</TableHead><TableHead>Activity</TableHead><TableHead>Method</TableHead>
            <TableHead className="text-right">Activity</TableHead><TableHead>Boundary</TableHead>
            <TableHead className="text-right">tCO₂e</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`mc-fuel-row-${r.id}`} data-selected={!!selected[r.id]}>
                {canWrite && <TableCell><input type="checkbox" checked={!!selected[r.id]} onChange={() => toggle(r.id)} aria-label="Select record" data-testid={`mc-fuel-select-${r.id}`} /></TableCell>}
                <TableCell className="text-xs">{r.period}</TableCell>
                <TableCell className="text-xs">{r.activity_type}<div className="text-[10px] text-muted-foreground">{r.asset_ref || "—"} · {flagLabel[r.control_flag] || r.control_flag}</div></TableCell>
                <TableCell className="text-xs capitalize">{r.method || "fuel"}</TableCell>
                <TableCell className="text-right font-mono text-xs" data-testid={`mc-fuel-value-${r.id}`}>{formatNum(r.value)} {r.unit}{r.distance_km ? ` · ${formatNum(r.distance_km)}km` : ""}</TableCell>
                <TableCell>{scopeBadge(r.derived_scope || r.scope, r.s3_cat)}<div className="text-[9px] text-muted-foreground mt-0.5">{r.factor_basis}</div></TableCell>
                <TableCell className="text-right font-mono text-xs">{fmt(r.co2e_tonnes)}</TableCell>
                <TableCell className="text-right font-mono text-xs" data-testid={`mc-fuel-cost-${r.id}`}>{r.currency_amount != null ? `${r.currency || "SGD"} ${formatNum(r.currency_amount)}` : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell>{r.status === "flagged" ? <Badge variant="destructive" className="text-[9px]">Flagged</Badge> : r.status === "unverified" ? <Badge variant="warning" className="text-[9px]" data-testid={`mc-fuel-unverified-${r.id}`} title="Imported/logged — pending review. Verify the import run to promote.">Unverified</Badge> : <Badge variant="success" className="text-[9px]">Verified</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => onLineage(r.id)} data-testid={`mc-lineage-${r.id}`}><GitBranch className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={canWrite ? 10 : 9} className="text-center text-muted-foreground py-8 text-sm">{records.length ? "No fuel records match these filters." : "No fuel records yet."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={confirmDelete} onOpenChange={(o) => { if (!o && !busy) setConfirmDelete(false); }}>
        <DialogContent className="w-[94vw] max-w-md" data-testid="mc-fuel-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-400"><Trash2 className="h-5 w-5" /> Delete fuel record(s)?</DialogTitle>
            <DialogDescription>
              This permanently removes <span className="font-semibold text-foreground" data-testid="mc-fuel-delete-count">{visibleSelectedCount}</span> selected fuel record{visibleSelectedCount === 1 ? "" : "s"} and updates Scope 1 totals. Each removal is logged in Change history with its values.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={busy} data-testid="mc-fuel-delete-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={busy} data-testid="mc-fuel-delete-confirm-btn">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Deleting…</> : <><Trash2 className="h-4 w-4" /> Delete {visibleSelectedCount}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="label-eyebrow text-[9px] mb-1">{label}</div>{children}</div>;
}

const GAS_LABEL = { co2: "CO₂", ch4: "CH₄", n2o: "N₂O" };

function GasSplit({ split }) {
  if (!split) return null;
  return (
    <div className="mt-2 border-t border-border/40 pt-2 space-y-1" data-testid="mc-gas-split">
      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
        <FlaskConical className="h-3 w-3 text-accent" /> Gas split · {split.gwp_set}
      </div>
      <div className="text-[10px] text-muted-foreground italic">{split.basis}</div>
      {["co2", "ch4", "n2o"].map((g) => (
        <div key={g} className="flex items-center justify-between text-[11px] font-mono">
          <span className="text-muted-foreground">{GAS_LABEL[g]} · {split.gases[g].mass_kg} kg × GWP {split.gases[g].gwp}</span>
          <span>{fmt(split.gases[g].co2e_kg, 2)} kgCO₂e</span>
        </div>
      ))}
      <div className="flex items-center justify-between text-[11px] font-mono font-bold">
        <span>Total</span><span className="text-accent">{fmt(split.co2e_tonnes)} tCO₂e</span>
      </div>
    </div>
  );
}

function Stat({ label, value, warn, testid }) {
  return (
    <div className="card-flat p-2.5" data-testid={testid}>
      <div className={`font-mono text-lg font-bold ${warn ? "text-[#FFB800]" : "text-foreground"}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Legend({ c, t }) {
  return <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: c }} /> {t}</span>;
}

const trendStatusBadge = (s) => {
  if (s === "anomaly") return <Badge variant="destructive" className="text-[9px]">Anomaly</Badge>;
  if (s === "missing") return <Badge variant="warning" className="text-[9px]">Missing</Badge>;
  if (s === "not_operational") return <Badge variant="outline" className="text-[9px] text-muted-foreground">Not yet operational</Badge>;
  return <Badge variant="success" className="text-[9px]">Reported</Badge>;
};

function TrendTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const m = payload[0].payload;
  if (m.status === "not_operational") return <div className="rounded-md border border-border bg-background p-2 text-xs">{m.period}: not yet operational</div>;
  return (
    <div className="rounded-md border border-border bg-background p-2 text-xs space-y-0.5">
      <div className="font-semibold">{m.period}</div>
      <div>{fmt(m.litres, 0)} litres · {fmt(m.co2e)} tCO₂e</div>
      {m.cost != null && <div>{m.currency || "SGD"} {fmt(m.cost, 0)}</div>}
      <div className="capitalize text-muted-foreground">{m.status === "ok" ? "reported" : m.status}</div>
    </div>
  );
}

function AssetTrend({ assets }) {
  const [assetId, setAssetId] = useState(assets[0]?.id || "");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const statusColor = { ok: "#00E65B", anomaly: "#FF4D4F", missing: "#FFB800", not_operational: "#3a3a3a" };

  useEffect(() => {
    if (!assetId) { setData(null); return; }
    setBusy(true);
    api.get(`/mobile-combustion/asset-trend?asset_id=${assetId}&year=${year}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setBusy(false));
  }, [assetId, year]);

  if (!assets.length) return <div className="card-flat p-8 text-center text-sm text-muted-foreground" data-testid="mc-trend-empty">Register a vehicle first to see its monthly trend.</div>;
  const chartData = (data?.months || []).map((m) => ({ ...m, label: m.period.slice(5), litresDisplay: m.status === "not_operational" ? 0 : m.litres }));

  return (
    <div className="space-y-4" data-testid="mc-asset-trend">
      <div className="flex flex-wrap items-center gap-2">
        <TrendingUp className="h-4 w-4 text-accent" />
        <select className={selectCls} style={{ maxWidth: 360 }} value={assetId} onChange={(e) => setAssetId(e.target.value)} data-testid="mc-trend-asset">
          {assets.map((a) => <option key={a.id} value={a.id}>{a.asset_ref} — {a.description}</option>)}
        </select>
        <select className={selectCls} style={{ width: 110 }} value={year} onChange={(e) => setYear(e.target.value)} data-testid="mc-trend-year">
          {[0, 1, 2, 3].map((d) => { const y = String(new Date().getFullYear() - d); return <option key={y} value={y}>{y}</option>; })}
        </select>
      </div>
      {busy && <div className="text-xs text-muted-foreground">Loading…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
            <Stat label="Total litres" value={fmt(data.total_litres, 0)} testid="mc-trend-total-litres" />
            <Stat label="Total tCO₂e" value={fmt(data.total_co2e)} />
            <Stat label="Months reported" value={`${data.reported_months}/${data.expected_months}`} testid="mc-trend-coverage" />
            <Stat label="Missing" value={data.missing_months} warn={data.missing_months > 0} />
            <Stat label="Anomalies" value={data.anomaly_months} warn={data.anomaly_months > 0} testid="mc-trend-anomalies" />
          </div>
          {data.asset.commissioned_month && <div className="text-[11px] text-muted-foreground" data-testid="mc-trend-commissioned">Operational from <span className="text-foreground font-medium">{data.asset.commissioned_month}</span>{data.asset.decommissioned_month ? ` to ${data.asset.decommissioned_month}` : ""} — earlier months show as “not yet operational” and are excluded from expected coverage.</div>}
          <div className="card-flat p-4" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#999" }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#999" }} />
                <Tooltip content={<TrendTip />} />
                <Bar yAxisId="l" dataKey="litresDisplay" name="Litres" radius={[3, 3, 0, 0]}>
                  {chartData.map((m, i) => <Cell key={i} fill={statusColor[m.status] || "#00E65B"} />)}
                </Bar>
                <Line yAxisId="r" type="monotone" dataKey="co2e" name="tCO₂e" stroke="#4DA6FF" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground mt-2 justify-center">
              <Legend c="#00E65B" t="Reported" /><Legend c="#FF4D4F" t="Anomaly (>2× / <0.3× median)" /><Legend c="#FFB800" t="Missing" /><Legend c="#3a3a3a" t="Not yet operational" />
            </div>
          </div>
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Litres</TableHead><TableHead className="text-right">tCO₂e</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.months.map((m) => (
                  <TableRow key={m.period} data-testid={`mc-trend-row-${m.period}`}>
                    <TableCell className="text-xs">{m.period}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{m.status === "not_operational" ? "—" : fmt(m.litres, 0)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{m.status === "not_operational" ? "—" : fmt(m.co2e)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{m.cost != null ? `${m.currency || "SGD"} ${fmt(m.cost, 0)}` : "—"}</TableCell>
                    <TableCell>{trendStatusBadge(m.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

function AssetDialog({ asset, ref_, onClose, onSaved }) {
  const isEdit = !!asset?.id;
  const [f, setF] = useState({
    asset_ref: asset?.asset_ref || "", description: asset?.description || "",
    asset_class: asset?.asset_class || "van", control_flag: asset?.control_flag || "owned",
    home_facility: asset?.home_facility || "", fuel_types: asset?.fuel_types || ["diesel"],
    transport_direction: asset?.transport_direction || "upstream",
    control_reason: asset?.control_reason || "", review_flag: asset?.review_flag || false,
    annual_distance_km: asset?.annual_distance_km ?? "", annual_engine_hours: asset?.annual_engine_hours ?? "",
    commissioned_month: asset?.commissioned_month || "", decommissioned_month: asset?.decommissioned_month || "",
  });
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const flagMeta = (ref_?.control_flags || []).find((c) => c.key === f.control_flag);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isEdit) api.get(`/mobile-combustion/assets/${asset.id}/history`).then((r) => setHistory(r.data.history || [])).catch(() => {});
  }, [isEdit, asset?.id]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, annual_distance_km: f.annual_distance_km === "" ? null : parseFloat(f.annual_distance_km),
        annual_engine_hours: f.annual_engine_hours === "" ? null : parseFloat(f.annual_engine_hours),
        commissioned_month: f.commissioned_month || null, decommissioned_month: f.decommissioned_month || null };
      if (isEdit) await api.patch(`/mobile-combustion/assets/${asset.id}`, payload);
      else await api.post("/mobile-combustion/assets", payload);
      toast.success(isEdit ? "Asset updated." : "Asset added.");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mc-asset-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit asset" : "Register asset"}</DialogTitle>
          <DialogDescription>Classify by operational control once — this decides the Scope 1 / Scope 3 boundary for its fuel.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Asset ID / plate"><input className={inputCls} value={f.asset_ref} onChange={(e) => set("asset_ref", e.target.value)} data-testid="mc-f-ref" /></Field>
          <Field label="Home facility"><input className={inputCls} value={f.home_facility} onChange={(e) => set("home_facility", e.target.value)} data-testid="mc-f-facility" /></Field>
          <div className="col-span-2"><Field label="Description"><input className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="mc-f-desc" /></Field></div>
          <Field label="Asset class">
            <select className={selectCls} value={f.asset_class} onChange={(e) => set("asset_class", e.target.value)} data-testid="mc-f-class">
              {(ref_?.vehicle_classes || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Primary fuel">
            <select className={selectCls} value={f.fuel_types[0]} onChange={(e) => set("fuel_types", [e.target.value])} data-testid="mc-f-fuel">
              {(ref_?.fuel_types || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <div className="col-span-2"><Field label="Operational control">
            <select className={selectCls} value={f.control_flag} onChange={(e) => set("control_flag", e.target.value)} data-testid="mc-f-control">
              {(ref_?.control_flags || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field></div>
          {flagMeta && (
            <div className="col-span-2 text-[11px] text-muted-foreground rounded-md border border-border p-2 flex items-start gap-2">
              <RouteIcon className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
              <span>Routes to <span className="font-semibold text-foreground">Scope {flagMeta.scope}{flagMeta.s3_cat ? ` · Cat ${flagMeta.s3_cat}` : ""}</span> — {flagMeta.desc}</span>
            </div>
          )}
          {f.control_flag === "hired_service" && (
            <Field label="Transport direction">
              <select className={selectCls} value={f.transport_direction} onChange={(e) => set("transport_direction", e.target.value)} data-testid="mc-f-direction">
                <option value="upstream">Upstream (Cat 4)</option><option value="downstream">Downstream (Cat 9)</option>
              </select>
            </Field>
          )}
          {isEdit && asset.control_flag !== f.control_flag && (
            <div className="col-span-2"><Field label="Reason for reclassification (recorded)">
              <input className={inputCls} value={f.control_reason} onChange={(e) => set("control_reason", e.target.value)} data-testid="mc-f-reason" placeholder="e.g. rental moved to hired-service mid-contract" />
            </Field></div>
          )}
          <label className="col-span-2 flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={f.review_flag} onChange={(e) => set("review_flag", e.target.checked)} data-testid="mc-f-review" />
            Flag for periodic control re-review
          </label>
          <Field label="Annual distance (km, optional)"><input className={inputCls} value={f.annual_distance_km} onChange={(e) => set("annual_distance_km", e.target.value)} data-testid="mc-f-annual-km" placeholder="for electrification view" /></Field>
          <Field label="Annual engine-hours (optional)"><input className={inputCls} value={f.annual_engine_hours} onChange={(e) => set("annual_engine_hours", e.target.value)} data-testid="mc-f-annual-hrs" placeholder="non-road machinery" /></Field>
          <Field label="Operational start month">
            <input type="month" className={inputCls} value={f.commissioned_month} onChange={(e) => set("commissioned_month", e.target.value)} data-testid="mc-f-commissioned" />
          </Field>
          <Field label="End / decommission month (optional)">
            <input type="month" className={inputCls} value={f.decommissioned_month} onChange={(e) => set("decommissioned_month", e.target.value)} data-testid="mc-f-decommissioned" />
          </Field>
          <div className="col-span-2 text-[11px] text-muted-foreground -mt-1">Months before the start month are treated as <span className="text-foreground">“not yet operational”</span> — they won't count as missing data or be flagged as anomalies.</div>
        </div>
        {isEdit && history.length > 0 && (
          <div className="border-t border-border/50 pt-3 mt-1" data-testid="mc-asset-history">
            <div className="label-eyebrow text-[9px] flex items-center gap-1 mb-2"><History className="h-3.5 w-3.5 text-accent" /> Reclassification history</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="text-[11px] flex gap-2" data-testid={`mc-history-${h.id}`}>
                  <div className="text-muted-foreground w-24 shrink-0">{(h.at || "").slice(0, 10)}</div>
                  <div>
                    {h.event === "reclassified"
                      ? <span><span className="font-medium">Reclassified</span> {h.from_label} → <span className="text-accent">{h.to_label}</span></span>
                      : <span className="font-medium">Created ({h.to_label})</span>}
                    {h.reason && <div className="text-muted-foreground italic">“{h.reason}”</div>}
                    <div className="text-[10px] text-muted-foreground">by {h.actor}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" className="flex-1" disabled={busy || !f.asset_ref || !f.description} onClick={save} data-testid="mc-asset-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save asset"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FuelDialog({ ref_, assets, onClose, onSaved }) {
  const curMonth = new Date().toISOString().slice(0, 7);
  const [f, setF] = useState({
    asset_id: assets[0]?.id || "", period: curMonth, fuel_type: "diesel", method: "fuel",
    value: "", unit: "litre", distance_km: "", engine_hours: "", cost_amount: "", currency: "SGD",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showGas, setShowGas] = useState(false);
  const [dupErr, setDupErr] = useState(null);   // set when a matching asset-month exists (409)
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const canPreview = f.asset_id && ((f.method === "distance" && f.distance_km) || (f.method !== "distance" && f.value));
    if (!canPreview) { setPreview(null); return; }
    const body = { ...f, value: parseFloat(f.value) || 0, distance_km: parseFloat(f.distance_km) || null, engine_hours: parseFloat(f.engine_hours) || null, period: f.period || curMonth };
    const t = setTimeout(() => {
      api.post("/mobile-combustion/calc-preview", body).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);

  const save = async (replace = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mobile-combustion/fuel", {
        ...f, value: parseFloat(f.value) || 0,
        distance_km: parseFloat(f.distance_km) || null, engine_hours: parseFloat(f.engine_hours) || null,
        cost_amount: f.cost_amount === "" ? null : parseFloat(f.cost_amount), currency: f.currency || "SGD",
        replace,
      });
      toast.success(data.superseded_count ? "Fuel record replaced the existing asset-month (Unverified — old kept in history)." : "Fuel record saved (Unverified — pending review).");
      onSaved();
    } catch (e) {
      if (e.response?.status === 409) { setDupErr(e.response?.data?.detail || "A record for this asset-month already exists."); }
      else { toast.error(apiErr(e, "Couldn't save the fuel record — please try again in a moment.")); }
    } finally { setBusy(false); }
  };

  const noAssets = !assets.length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mc-fuel-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Log mobile-combustion fuel</DialogTitle>
          <DialogDescription>Scope is derived automatically from the asset's control classification.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Asset">
            {noAssets ? (
              <div className="rounded-md border border-[#FFB800]/50 bg-[#FFB800]/10 p-3 text-xs text-[#FFB800] flex items-start gap-2" data-testid="mc-fuel-no-assets">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>No vehicles or machinery registered yet. <span className="text-foreground">Register an asset first</span> (Asset Register → Add asset), then log its fuel here.</span>
              </div>
            ) : (
              <select className={selectCls} value={f.asset_id} onChange={(e) => set("asset_id", e.target.value)} data-testid="mc-fuel-asset">
                {assets.map((a) => <option key={a.id} value={a.id}>{a.asset_ref} — {a.description} ({flagLabel[a.control_flag]})</option>)}
              </select>
            )}
          </Field></div>
          <Field label="Period (YYYY-MM)"><input className={inputCls} value={f.period} onChange={(e) => set("period", e.target.value)} placeholder="2026-05" data-testid="mc-fuel-period" /></Field>
          <Field label="Method">
            <select className={selectCls} value={f.method} onChange={(e) => set("method", e.target.value)} data-testid="mc-fuel-method">
              {(ref_?.methods || []).filter((m) => m.key !== "spend").map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </Field>
          {f.method === "fuel" ? (
            <>
              <Field label="Fuel type">
                <select className={selectCls} value={f.fuel_type} onChange={(e) => set("fuel_type", e.target.value)} data-testid="mc-fuel-type">
                  {(ref_?.fuel_types || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Quantity">
                <div className="flex gap-1">
                  <input className={inputCls} value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="mc-fuel-value" />
                  <select className={selectCls} value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="mc-fuel-unit" style={{ width: 90 }}>
                    {["litre", "kg", "kWh", "GJ"].map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </Field>
            </>
          ) : (
            <Field label="Distance (km)"><input className={inputCls} value={f.distance_km} onChange={(e) => set("distance_km", e.target.value)} data-testid="mc-fuel-distance" /></Field>
          )}
          {f.method === "fuel" && (
            <Field label="Odometer distance (km, optional)"><input className={inputCls} value={f.distance_km} onChange={(e) => set("distance_km", e.target.value)} data-testid="mc-fuel-odo" /></Field>
          )}
          <Field label="Engine hours (optional)"><input className={inputCls} value={f.engine_hours} onChange={(e) => set("engine_hours", e.target.value)} data-testid="mc-fuel-hours" /></Field>
          <Field label="Invoice cost (optional)">
            <div className="flex gap-1">
              <input className={inputCls} value={f.cost_amount} onChange={(e) => set("cost_amount", e.target.value)} data-testid="mc-fuel-cost" placeholder="from the fuel invoice" />
              <select className={selectCls} value={f.currency} onChange={(e) => set("currency", e.target.value)} data-testid="mc-fuel-currency" style={{ width: 90 }}>
                {["SGD", "USD", "MYR", "EUR", "GBP"].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </Field>
        </div>
        {preview && (
          <div className="rounded-md border border-border p-3 text-xs space-y-1" data-testid="mc-fuel-preview">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Routes to</span>
              {scopeBadge(preview.scope, preview.s3_cat)}
            </div>
            {preview.conversion_note && <div className="text-muted-foreground">↳ {preview.conversion_note}</div>}
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Factor</span><span className="font-mono">{preview.factor_code} · {preview.factor_basis}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Emissions</span><span className="font-mono text-accent font-bold">{fmt(preview.co2e_tonnes)} tCO₂e</span></div>
            {preview.sanity_warning && <div className="text-[#FFB800] flex items-start gap-1 mt-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{preview.sanity_warning}</div>}
            {preview.gas_split && (
              <>
                <button type="button" onClick={() => setShowGas((s) => !s)} className="text-[10px] text-accent hover:underline mt-1" data-testid="mc-toggle-gas">
                  {showGas ? "Hide" : "Show"} CO₂ / CH₄ / N₂O gas breakdown
                </button>
                {showGas && <GasSplit split={preview.gas_split} />}
              </>
            )}
          </div>
        )}
        {dupErr && (
          <div className="rounded-md border border-[#FFB800]/50 bg-[#FFB800]/10 p-3 text-xs text-[#FFB800] flex items-start gap-2" data-testid="mc-fuel-dup-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>{dupErr} <span className="block mt-1 text-foreground">If this is a correction, choose <b>Replace</b> — the old record is kept in history but excluded from totals.</span></div>
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          {dupErr ? (
            <Button variant="primary" className="flex-1" disabled={busy} onClick={() => save(true)} data-testid="mc-fuel-replace-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Replace & save"}
            </Button>
          ) : (
            <Button variant="primary" className="flex-1" disabled={busy || noAssets || !f.asset_id || !f.period} onClick={() => save(false)} data-testid="mc-fuel-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save record"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SpendDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ period: "", description: "", amount_sgd: "", transport_direction: "upstream", supplier: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/mobile-combustion/spend", { ...f, amount_sgd: parseFloat(f.amount_sgd) || 0 });
      toast.success("Hired-transport spend recorded (Scope 3).");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="mc-spend-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Hired transport — spend-based</DialogTitle>
          <DialogDescription>For lump-sum haulage with no litre-level data. Routed to Scope 3 (Cat 4/9) via a spend-based factor.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Period (YYYY-MM)"><input className={inputCls} value={f.period} onChange={(e) => set("period", e.target.value)} placeholder="2026-05" data-testid="mc-spend-period" /></Field>
          <Field label="Amount (S$)"><input className={inputCls} value={f.amount_sgd} onChange={(e) => set("amount_sgd", e.target.value)} data-testid="mc-spend-amount" /></Field>
          <div className="col-span-2"><Field label="Description"><input className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="mc-spend-desc" /></Field></div>
          <Field label="Supplier"><input className={inputCls} value={f.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="mc-spend-supplier" /></Field>
          <Field label="Direction">
            <select className={selectCls} value={f.transport_direction} onChange={(e) => set("transport_direction", e.target.value)} data-testid="mc-spend-direction">
              <option value="upstream">Upstream (Cat 4)</option><option value="downstream">Downstream (Cat 9)</option>
            </select>
          </Field>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" className="flex-1" disabled={busy || !f.period || !f.amount_sgd} onClick={save} data-testid="mc-spend-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ mode = "assets", onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);       // assets import result
  const [preview, setPreview] = useState(null);      // fuel preview
  const [replaceKeys, setReplaceKeys] = useState({});// {label_key: true}
  const [committed, setCommitted] = useState(null);  // fuel commit result
  const isFuel = mode === "fuel";
  const templateUrl = isFuel ? "/mobile-combustion/fuel/import-template.csv" : "/mobile-combustion/assets/import-template.csv";
  const templateName = isFuel ? "mobile_fuel_template.csv" : "mobile_assets_template.csv";

  const downloadTemplate = async () => {
    try {
      const r = await api.get(templateUrl, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = templateName; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 4000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const uploadAssets = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/mobile-combustion/assets/import", fd);
      setResult(data);
      toast.success(`${data.created} asset(s) imported.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const previewFuel = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/mobile-combustion/fuel/import-preview", fd);
      setPreview(data);
      if (!data.summary.new && !data.summary.duplicate) toast.warning("Nothing to import — every row was rejected.");
      else if (!data.summary.new && data.summary.duplicate) toast.warning("All rows are already present — nothing new to commit.");
      else toast.success(`Preview ready: ${data.summary.new} new · ${data.summary.duplicate} already present · ${data.summary.rejected} rejected`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const commitFuel = async () => {
    setBusy(true);
    try {
      const keys = Object.keys(replaceKeys).filter((k) => replaceKeys[k]);
      const { data } = await api.post("/mobile-combustion/fuel/import-commit", { import_id: preview.import_id, replace_keys: keys });
      setCommitted(data);
      toast.success(`Imported ${data.committed} fuel record(s) as Unverified${data.replaced ? ` · ${data.replaced} replaced` : ""}. Find it in Data Management → Import Runs to Undo or Verify.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleReplace = (key) => setReplaceKeys((p) => ({ ...p, [key]: !p[key] }));
  const dupKey = (d) => `${d.asset_ref}|${d.period}|${d.fuel_type}`;
  const done = isFuel ? !!committed : !!result;
  const nothingToCommit = isFuel && preview && !preview.summary.new &&
    !Object.values(replaceKeys).some(Boolean);

  return (
    <Dialog open onOpenChange={(o) => !o && (done ? onDone() : onClose())}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="mc-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isFuel ? "Import fuel records" : "Import assets"}</DialogTitle>
          <DialogDescription>{isFuel
            ? "Upload a CSV or XLSX of monthly fuel. You'll see a preview — new vs already-present vs rejected — before anything is saved. Imported rows land Unverified."
            : "Upload a CSV or XLSX to bulk-register vehicles & machinery. Each row is classified by operational control."}</DialogDescription></DialogHeader>

        {!preview && !result && (
          <>
            <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline" data-testid="mc-import-template-btn">
              <Download className="h-3.5 w-3.5" /> Download CSV template
            </button>
            <input type="file" accept=".csv,.xlsx,.xlsm" onChange={(e) => { setFile(e.target.files[0]); setResult(null); setPreview(null); setCommitted(null); }} data-testid="mc-import-file"
              className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-accent/20 file:px-3 file:py-1.5 file:text-accent" />
          </>
        )}

        {/* ---- FUEL preview ---- */}
        {isFuel && preview && !committed && (
          <div className="space-y-3" data-testid="mc-fuel-preview-summary">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md border border-[#00E65B]/40 p-2"><div className="font-mono text-xl font-bold text-[#00E65B]" data-testid="mc-preview-new">{preview.summary.new}</div><div className="text-[10px] text-muted-foreground">new</div></div>
              <div className="rounded-md border border-[#FFB800]/40 p-2"><div className="font-mono text-xl font-bold text-[#FFB800]" data-testid="mc-preview-dup">{preview.summary.duplicate}</div><div className="text-[10px] text-muted-foreground">already present</div></div>
              <div className="rounded-md border border-red-500/40 p-2"><div className="font-mono text-xl font-bold text-red-400" data-testid="mc-preview-rejected">{preview.summary.rejected}</div><div className="text-[10px] text-muted-foreground">rejected</div></div>
            </div>
            {preview.duplicate_rows?.length > 0 && (
              <div className="rounded-md border border-[#FFB800]/40 p-2.5 text-xs space-y-1" data-testid="mc-preview-dup-list">
                <div className="font-semibold text-[#FFB800]">Already present — skipped unless you Replace:</div>
                {preview.duplicate_rows.map((d, i) => (
                  <label key={i} className="flex items-center gap-2 cursor-pointer" data-testid={`mc-dup-row-${i}`}>
                    <input type="checkbox" checked={!!replaceKeys[dupKey(d)]} onChange={() => toggleReplace(dupKey(d))} data-testid={`mc-dup-replace-${i}`} />
                    <span>{d.asset_ref} · {d.period} · {d.fuel_type} · {formatNum(d.value)} {d.unit} <span className="text-muted-foreground">(Replace supersedes the existing record)</span></span>
                  </label>
                ))}
              </div>
            )}
            {preview.rejected_rows?.length > 0 && (
              <div className="rounded-md border border-red-500/40 p-2.5 text-xs space-y-0.5" data-testid="mc-preview-rejected-list">
                <div className="font-semibold text-red-400">Rejected:</div>
                {preview.rejected_rows.map((d, i) => (
                  <div key={i} className="text-[11px]" data-testid={`mc-rejected-row-${i}`}>Row {d.num}{d.asset_ref ? ` (${d.asset_ref}${d.period ? `, ${d.period}` : ""})` : ""}: {d.reason}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---- FUEL committed ---- */}
        {isFuel && committed && (
          <div className="rounded-md border border-border p-3 text-xs space-y-1" data-testid="mc-fuel-commit-result">
            <div className="text-[#00E65B]">✓ Committed {committed.committed} fuel record(s) as Unverified.</div>
            {committed.replaced > 0 && <div className="text-[#FFB800]">↺ {committed.replaced} record(s) replaced (old superseded, kept in history).</div>}
            {committed.skipped_duplicates > 0 && <div className="text-muted-foreground">Skipped {committed.skipped_duplicates} already-present row(s) — no double-count.</div>}
            {committed.rejected > 0 && <div className="text-muted-foreground">Rejected {committed.rejected} row(s).</div>}
            <div className="text-muted-foreground pt-1">Open Data Management → Import Runs to Undo or Verify this batch.</div>
          </div>
        )}

        {/* ---- ASSETS result ---- */}
        {!isFuel && result && (
          <div className="rounded-md border border-border p-3 text-xs space-y-1" data-testid="mc-import-result">
            <div className="text-[#00E65B]">✓ Created: {result.created}</div>
            {result.skipped > 0 && <div className="text-muted-foreground">Skipped (already exist): {result.skipped}</div>}
            {result.errors?.length > 0 && (
              <div className="text-red-400">{result.errors.length} error(s):
                {result.errors.map((er, i) => <div key={i} className="text-[10px]">Row {er.row}: {er.msg}</div>)}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={() => (done ? onDone() : onClose())}>{done ? "Done" : "Cancel"}</Button>
          {!isFuel && !result && (
            <Button variant="primary" className="flex-1" disabled={busy || !file} onClick={uploadAssets} data-testid="mc-import-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Upload</>}
            </Button>
          )}
          {isFuel && !preview && (
            <Button variant="primary" className="flex-1" disabled={busy || !file} onClick={previewFuel} data-testid="mc-import-preview-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Preview</>}
            </Button>
          )}
          {isFuel && preview && !committed && (
            <Button variant="primary" className="flex-1" disabled={busy || nothingToCommit} onClick={commitFuel} data-testid="mc-import-commit-btn" title={nothingToCommit ? "Nothing to commit — all rows are already present" : ""}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> Confirm import</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BackfillRow({ row, assets, onDone, canWrite }) {
  const [aid, setAid] = useState(assets[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    setBusy(true);
    try { await api.post("/mobile-combustion/backfill", { assignments: { [row.id]: aid } }); toast.success("Assigned & re-routed."); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 border-t border-border/50 py-2 text-xs" data-testid={`mc-backfill-${row.id}`}>
      <div className="flex-1 min-w-0"><span className="font-medium">{row.activity_type}</span> · {row.period} · {fmt(row.value, 0)} {row.unit}</div>
      {canWrite && <>
        <select className={selectCls} style={{ width: 200 }} value={aid} onChange={(e) => setAid(e.target.value)}>
          {assets.map((a) => <option key={a.id} value={a.id}>{a.asset_ref}</option>)}
        </select>
        <Button size="sm" variant="primary" disabled={busy || !aid} onClick={assign} data-testid={`mc-backfill-btn-${row.id}`}>Assign</Button>
      </>}
    </div>
  );
}

function LineageDialog({ pointId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/data/${pointId}/provenance`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [pointId]);
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/data/${pointId}/lineage.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `lineage_${pointId}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 4000);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const b = data?.boundary;
  const c = data?.calculation;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mc-lineage-dialog">
        {!data ? <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><GitBranch className="h-5 w-5 text-accent" /> Record lineage</DialogTitle>
              <DialogDescription>{data.data_point.activity_type} · {data.data_point.period}</DialogDescription></DialogHeader>

            {b && (
              <div className="rounded-lg border border-accent/40 bg-surface/40 p-3 space-y-1.5" data-testid="mc-lineage-boundary">
                <div className="label-eyebrow text-[9px] flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Scope boundary — operational control</div>
                <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Asset</span><span>{b.asset_description} ({b.asset_ref})</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Control classification</span><span className="font-medium">{b.control_label}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Routed to</span>{scopeBadge(b.scope, b.s3_cat)}</div>
                <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Factor basis</span><span>{b.factor_basis_label}</span></div>
                <p className="text-[11px] text-muted-foreground pt-1">{b.note}</p>
                {b.gas_split && <GasSplit split={b.gas_split} />}
              </div>
            )}

            {c && c.calculated && (
              <div className="space-y-1.5 pt-2">
                <div className="label-eyebrow text-[9px] flex items-center gap-1"><Gauge className="h-3.5 w-3.5 text-accent" /> Calculation</div>
                <div className="text-xs text-muted-foreground">{c.formula}</div>
                {c.steps.map((s, i) => (
                  <div key={i} className="text-xs font-mono">Step {i + 1}: {s.expression} → {s.result}</div>
                ))}
                <div className="text-[11px] text-muted-foreground pt-1">Factor: {c.emission_factor.name} ({c.emission_factor.code}) · {c.emission_factor.source}</div>
              </div>
            )}

            <div className="flex items-center gap-2 text-[11px] text-muted-foreground pt-2 border-t border-border/50">
              <FileText className="h-3.5 w-3.5" /> Captured via {data.provenance.capture_method} by {data.provenance.captured_by || "—"}
            </div>
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="mc-lineage-pdf-btn"><Download className="h-3.5 w-3.5" /> Download lineage PDF</Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
